import { useState, useEffect, useCallback, useMemo } from "react";
import CenterPanel from "./components/pitch/panels/CenterPanel";
import PitchNav from "./components/pitch/panels/PitchNav";
import MobilePitchView from "./components/pitch/mobile/MobileJourney";
import { ThemeProvider, useTheme } from "./components/pitch/context/ThemeContext";
import { AirlineProvider, useAirline } from "./components/pitch/context/AirlineContext";
import { airlines as defaultAirlines } from "./components/pitch/data/airlines";
import { getSlidesForAirline } from "./components/pitch/data/slides";
import { PriceVisibilityProvider } from "./components/pitch/context/PriceVisibilityContext";

const PitchModeContent = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { isDark } = useTheme();
  const { airline } = useAirline();
  const [isMobile, setIsMobile] = useState(false);
  const airlineId = airline?.id || "default";

  const slideList = useMemo(() => getSlidesForAirline(airline), [airline]);
  const totalSlides = slideList.length;

  useEffect(() => {
    setCurrentSlide(0);
  }, [airlineId]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || isMobile) return;
    if (e.target.closest?.('[role="tablist"]')) return;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
      case "PageDown":
      case " ":
        e.preventDefault();
        setCurrentSlide((p) => (p < totalSlides - 1 ? p + 1 : p));
        break;
      case "ArrowLeft":
      case "ArrowUp":
      case "PageUp":
        e.preventDefault();
        setCurrentSlide((p) => (p > 0 ? p - 1 : p));
        break;
      case "Home":
        e.preventDefault();
        setCurrentSlide(0);
        break;
      case "End":
        e.preventDefault();
        setCurrentSlide(totalSlides - 1);
        break;
      default:
        break;
    }
  }, [isMobile, totalSlides]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (isMobile) return <MobilePitchView />;

  return (
    <div
      className="h-screen w-screen overflow-hidden transition-colors duration-500"
      style={{
        backgroundColor: isDark ? "#0b0b0d" : "var(--k-canvas)",
        color: isDark ? "var(--k-ink-invert)" : "var(--k-ink)",
      }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <PitchNav currentSlide={currentSlide} setCurrentSlide={setCurrentSlide} slideList={slideList} />

      <main className="h-full pt-14">
        <CenterPanel currentSlideId={currentSlide} slideList={slideList} />
      </main>
    </div>
  );
};

const PitchMode = ({ airline }) => (
  <ThemeProvider>
    <AirlineProvider airline={airline || defaultAirlines.default}>
      <PriceVisibilityProvider>
        <PitchModeContent />
      </PriceVisibilityProvider>
    </AirlineProvider>
  </ThemeProvider>
);

export default PitchMode;
